import { useTranslation } from "react-i18next";
import { Progress } from "ui";

import { RevenueByChannel, RevenueByReseller } from "../../../../apis";
import { FinancialSalesChannel } from "../../../../constants";
import { barPercent, formatXof, shareOf } from "../../financialFormat";

import styles from "./ChannelBreakdown.module.scss";

const CHANNEL_META: Record<
  FinancialSalesChannel,
  { label: string; color: string }
> = {
  [FinancialSalesChannel.WhatsApp]: { label: "WhatsApp", color: "#16A34A" },
  [FinancialSalesChannel.AppClient]: {
    label: "App · client",
    color: "#2F6FD0"
  },
  [FinancialSalesChannel.AppReseller]: {
    label: "App · reseller",
    color: "#7A5AF8"
  },
  [FinancialSalesChannel.Advertising]: {
    label: "Advertising",
    color: "#E5346E"
  }
};

interface ChannelBreakdownProps {
  channels: RevenueByChannel[];
  topResellers: RevenueByReseller[];
  totalValue: number;
}

const ChannelBreakdown = ({
  channels,
  topResellers,
  totalValue
}: ChannelBreakdownProps) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className={styles.channels}>
        {channels.map((channel) => {
          const meta = CHANNEL_META[channel.channel];
          const share = shareOf(channel.netRevenue, totalValue);

          return (
            <div className={styles.channelCard} key={channel.channel}>
              <div className={styles.channelCard__label}>
                <span
                  className={styles.channelCard__dot}
                  style={{ background: meta.color }}
                />
                {t(meta.label)}
              </div>
              <div className={styles.channelCard__amount}>
                {formatXof(channel.netRevenue)}
              </div>
              <div className={styles.channelCard__share}>
                <div className={styles.channelCard__shareBar}>
                  <Progress
                    percentage={barPercent(share)}
                    transparentBarBackground
                  />
                </div>
                <span className={styles.channelCard__shareLabel}>
                  {share === null ? "—" : `${share.toFixed(1)}%`} ·{" "}
                  {channel.units} {t("units")}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {topResellers.length > 0 && (
        <div className={styles.topResellers}>
          <span className={styles.topResellers__label}>
            {t("Top resellers")}
          </span>
          {topResellers.slice(0, 3).map((reseller) => (
            <span
              className={styles.topResellers__item}
              key={reseller.resellerId}
            >
              <span className={styles.topResellers__dot} />
              {reseller.firstName} {reseller.lastName}
              {reseller.city ? ` — ${reseller.city}` : ""}{" "}
              <strong>{formatXof(reseller.netRevenue)}</strong>{" "}
              <span className={styles.topResellers__units}>
                · {reseller.units} {t("units")}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChannelBreakdown;
